import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shareMyWorkout } from '../../ducks/Workout/actions';
import moment from 'moment';
import { Loading } from '../../components/Loading';
import { Button, Tooltip } from 'antd';
import {
  DownloadOutlined,
  ShareAltOutlined,
  InstagramOutlined,
  TwitterOutlined,
} from '@ant-design/icons';
import { Canvas, Rect, Circle, Text, Gradient } from 'fabric';
import { toast } from '../Toast';
import { ArrowLeftOutlined } from '@ant-design/icons';
import 'react-loading-skeleton/dist/skeleton.css';
import NubodLogo from '../../assets/logos/nubod-logo-black.png';
import ShareCardModal from './ShareCardModal';
import { toDisplayWeight, unitForMetric } from '../../utils/units';
import './index.css';

export const SharedWorkout = ({ match }) => {
  const dispatch = useDispatch();
  const { workoutToShare, getWorkoutsLoading } = useSelector(
    (state) => state.workouts
  );
  const { user } = useSelector((state) => state.auth);
  const { token } = match.params;
  const [, setIsGenerating] = useState(false);

  console.log(user, workoutToShare);
  useEffect(() => {
    dispatch(shareMyWorkout(token));
  }, [dispatch, token]);

  // DISPLAY UNIT for this PUBLIC page. Weights are stored canonical POUNDS;
  // we convert for display only. There's no logged-in viewer to read a unit
  // from, so the unit must come from the WORKOUT OWNER's profile.
  //
  // FLAG: the share endpoint (GET /api/workout/share/:token) returns the
  // Workout doc with `client` projected to NAME ONLY (User.findById(id,
  // 'name')) — `isMetric` lives on the Profile model and is NOT in this
  // payload. So `workoutToShare.client.isMetric` is currently undefined and
  // `unitForMetric(undefined)` resolves to 'lb', preserving today's behavior.
  // To honor kg for metric owners, plumb the owner's `isMetric` through the
  // share endpoint (e.g. project it onto `client`); this line then needs no
  // change and the page becomes unit-correct automatically.
  const unit = unitForMetric(workoutToShare?.client?.isMetric);

  // Workouts created via the activity-log modal (cardio, mobility, class,
  // sport, other) carry no per-set data — render a different summary for
  // them rather than showing "0 exercises · 0 sets · 0 lb". Legacy records
  // without `type` are treated as strength.
  const isStrengthWorkout = (w) => !w || (w.type || 'strength') === 'strength';

  // The share card (story + post) for this workout.
  const [shareCardOpen, setShareCardOpen] = useState(false);
  const handleShareImage = (workout) => {
    if (workout?.token) setShareCardOpen(true);
  };

  // Coerce a possibly-string numeric field (older logs sometimes stored
  // reps/load as strings, which corrupts `+` reduction via string
  // concatenation). Falls back to 0 for null/undefined/empty/NaN.
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Calculate workout summary stats
  const getWorkoutSummary = () => {
    if (!workoutToShare) return {};

    const exercises = Array.isArray(workoutToShare.exercises)
      ? workoutToShare.exercises
      : [];

    const totalExercises = exercises.length;
    // Count only sets that have at least one logged value (reps OR load).
    // Empty/skeleton sets shouldn't inflate the "Total Sets" tile.
    const totalSets = exercises.reduce((acc, exercise) => {
      const data = Array.isArray(exercise.data) ? exercise.data : [];
      return acc + data.filter((s) => num(s.reps) > 0 || num(s.load) > 0).length;
    }, 0);
    const totalReps = exercises.reduce((acc, exercise) => {
      const data = Array.isArray(exercise.data) ? exercise.data : [];
      return acc + data.reduce((setAcc, set) => setAcc + num(set.reps), 0);
    }, 0);
    // Volume math skips sets carrying a `duration` field — those are
    // time-tracked exercises (plank, dead hang, carries) and don't
    // contribute to tonnage. Mirrors helpers/exercises/volume.js on
    // the backend.
    const totalVolume = exercises.reduce((acc, exercise) => {
      const data = Array.isArray(exercise.data) ? exercise.data : [];
      return (
        acc +
        data.reduce((setAcc, set) => {
          if (num(set.duration) > 0) return setAcc;
          return setAcc + num(set.load) * num(set.reps);
        }, 0)
      );
    }, 0);
    // Prefer the recorded workout duration (seconds) when available; fall
    // back to a 2.5 min/set estimate so older records that didn't store
    // duration still show something reasonable.
    const recordedSeconds = num(workoutToShare.duration);
    const estimatedDuration = recordedSeconds > 0
      ? Math.max(1, Math.round(recordedSeconds / 60))
      : Math.round(totalSets * 2.5);

    return {
      totalExercises,
      totalSets,
      totalReps,
      totalVolume,
      estimatedDuration,
    };
  };

  const displayExerciseData = (exercise) => {
    const data = Array.isArray(exercise.data) ? exercise.data : [];
    // Same logged-only filter as the workout summary so per-exercise
    // tiles agree with the totals at the top of the page.
    const loggedSets = data.filter(
      (s) => num(s.reps) > 0 || num(s.load) > 0
    );
    const totalSets = loggedSets.length;
    const totalReps = data.reduce((acc, curr) => acc + num(curr.reps), 0);
    const heaviestLoad = data.reduce(
      (maxLoad, curr) => Math.max(maxLoad, num(curr.load)),
      0
    );
    const exerciseVolume = data.reduce(
      (acc, set) => acc + num(set.load) * num(set.reps),
      0
    );

    return (
      <>
        <div className='exercise-stats-grid mb-2'>
          <div className='stat-item'>
            <div className='stat-value'>{totalSets}</div>
            <div className='stat-label'>Sets</div>
          </div>
          <div className='stat-item'>
            <div className='stat-value'>{totalReps}</div>
            <div className='stat-label'>Reps</div>
          </div>
          <div className='stat-item'>
            <div className='stat-value'>
              {toDisplayWeight(heaviestLoad, unit)}
            </div>
            <div className='stat-label'>Max Weight</div>
          </div>
          <div className='stat-item'>
            <div className='stat-value'>
              {Math.round(toDisplayWeight(exerciseVolume, unit))}
            </div>
            <div className='stat-label'>Volume</div>
          </div>
        </div>

        {/* Individual Set Details — only logged sets render. Skeletons
            (set with no reps and no load) would read as "0 lbs · 0 reps"
            otherwise, which is misleading on a public-facing page. */}
        {loggedSets.length > 0 && (
          <div className='set-details-container'>
            <h4 className='set-details-title'>Set Details</h4>
            <div className='set-details-grid'>
              {loggedSets.map((set, setIndex) => {
                const reps = num(set.reps);
                const load = num(set.load);
                const rir = num(set.rir);
                return (
                  <div key={setIndex} className='set-detail-item'>
                    <div className='set-number'>Set {setIndex + 1}</div>
                    <div className='set-info'>
                      {load > 0 && (
                        <span className='set-stat weight'>
                          {toDisplayWeight(load, unit)} {unit}
                        </span>
                      )}
                      {reps > 0 && (
                        <span className='set-stat reps'>{reps} reps</span>
                      )}
                      {rir > 0 ? (
                        <span className='set-stat rir'>RIR {rir}</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderExercises = () => {
    if (!workoutToShare || !Array.isArray(workoutToShare.exercises)) return null;
    return (
      workoutToShare.exercises.map((exercise, index) => {
        return (
          <div className='exercise-card' key={exercise._id}>
            <div className='exercise-header'>
              <div className='exercise-number'>{index + 1}</div>
              <h3 className='exercise-name'>{exercise.name}</h3>
            </div>
            {displayExerciseData(exercise)}
          </div>
        );
      })
    );
  };

  const generateProfessionalImage = async (format) => {
    setIsGenerating(true);
    try {
      // Set dimensions based on format
      let width, height;
      switch (format) {
        case 'instagram-story':
          width = 1080;
          height = 1920;
          break;
        case 'instagram-post':
          width = 1080;
          height = 1080;
          break;
        case 'twitter':
          width = 1200;
          height = 675;
          break;
        default:
          width = 1080;
          height = 1080;
      }

      // Create canvas with higher quality settings
      const canvas = new Canvas('temp-canvas', {
        width,
        height,
        enableRetinaScaling: true,
      });

      // 🎯 HELPER FUNCTIONS FOR EASY ALIGNMENT
      const centerText = (text, y, fontSize, fontWeight = 'normal') => ({
        left: width / 2,
        top: y,
        text,
        fontSize,
        fontWeight,
        fill: '#ffffff',
        fontFamily: 'Montserrat, sans-serif',
        originX: 'center',
        originY: 'center',
      });

      const leftAlignText = (text, x, y, fontSize, fontWeight = 'normal') => ({
        left: x,
        top: y,
        text,
        fontSize,
        fontWeight,
        fill: '#ffffff',
        fontFamily: 'Montserrat, sans-serif',
        originX: 'left',
        originY: 'center',
      });

      const createCard = (x, y, cardWidth, cardHeight, opacity = 0.08) => ({
        left: x,
        top: y,
        width: cardWidth,
        height: cardHeight,
        fill: `rgba(255, 255, 255, ${opacity})`,
        rx: 30,
        ry: 30,
        stroke: 'rgba(255, 255, 255, 0.15)',
        strokeWidth: 1,
      });

      const createBadge = (x, y, width, height) => ({
        left: x,
        top: y,
        width,
        height,
        fill: 'rgba(39, 97, 241, 0.9)',
        rx: height / 2,
        ry: height / 2,
      });

      const calculateSpacing = (startY, itemHeight, gap, count) => {
        const positions = [];
        for (let i = 0; i < count; i++) {
          positions.push(startY + i * (itemHeight + gap));
        }
        return positions;
      };

      // Enhanced background gradient
      const background = new Rect({
        left: 0,
        top: 0,
        width: width,
        height: height,
        fill: new Gradient({
          type: 'linear',
          coords: { x1: 0, y1: 0, x2: width, y2: height },
          colorStops: [
            { offset: 0, color: '#1a1a2e' },
            { offset: 0.3, color: '#16213e' },
            { offset: 0.7, color: '#0f3460' },
            { offset: 1, color: '#533483' },
          ],
        }),
      });
      canvas.add(background);

      // Add subtle geometric patterns with perfect positioning
      const addGeometricPatterns = () => {
        // Top right corner accent - perfectly positioned
        const accentCircle = new Circle({
          left: width - 180,
          top: 60,
          radius: 140,
          fill: 'rgba(39, 97, 241, 0.12)',
          selectable: false,
          evented: false,
        });
        canvas.add(accentCircle);

        // Bottom left accent - perfectly positioned
        const bottomAccent = new Circle({
          left: 60,
          top: height - 220,
          radius: 120,
          fill: 'rgba(255, 0, 102, 0.08)',
          selectable: false,
          evented: false,
        });
        canvas.add(bottomAccent);

        // Floating dots with strategic positioning
        const dotPositions = [
          { left: 200, top: 300 },
          { left: 800, top: 400 },
          { left: 150, top: 800 },
          { left: 900, top: 900 },
          { left: 300, top: 1200 },
          { left: 750, top: 1400 },
          { left: 100, top: 1600 },
          { left: 850, top: 1700 },
        ];

        dotPositions.forEach((pos) => {
          const dot = new Circle({
            left: pos.left,
            top: pos.top,
            radius: Math.random() * 6 + 3,
            fill: 'rgba(255, 255, 255, 0.08)',
            selectable: false,
            evented: false,
          });
          canvas.add(dot);
        });
      };
      addGeometricPatterns();

      // 🎯 HEADER SECTION - Now much easier to align!
      const headerCard = createCard(60, 100, width - 120, 160);
      const headerBg = new Rect(headerCard);
      canvas.add(headerBg);

      // Badge - perfectly centered within header
      const badgeCard = createBadge(120, 130, 240, 45);
      const badgeBg = new Rect(badgeCard);
      canvas.add(badgeBg);

      const badge = new Text('🏆 WORKOUT COMPLETED', {
        ...centerText('🏆 WORKOUT COMPLETED', 152, 16, 'bold'),
        left: 140, // Slight adjustment for badge positioning
        originX: 'left',
      });
      canvas.add(badge);

      // Workout name - perfectly centered
      const workoutName = new Text(workoutToShare.name, {
        ...centerText(workoutToShare.name, 200, 42, 'bold'),
      });
      canvas.add(workoutName);

      // Date - perfectly centered below name
      const date = new Text(
        `📅 ${moment(workoutToShare.date).format('MMMM DD, YYYY')}`,
        centerText(
          `📅 ${moment(workoutToShare.date).format('MMMM DD, YYYY')}`,
          250,
          22
        )
      );
      canvas.add(date);

      // 🎯 STATS SECTION - Easy grid layout!
      const statsCard = createCard(60, 300, width - 120, 180);
      const statsBg = new Rect(statsCard);
      canvas.add(statsBg);

      const statsTitle = new Text('📊 WORKOUT SUMMARY', {
        ...centerText('📊 WORKOUT SUMMARY', 330, 26, 'bold'),
      });
      canvas.add(statsTitle);

      // Enhanced stats grid with perfect alignment - icon on left, stat on right
      const workoutSummary = getWorkoutSummary();
      const stats = [
        {
          label: 'Exercises',
          value: workoutSummary.totalExercises,
          icon: '💪',
        },
        { label: 'Total Sets', value: workoutSummary.totalSets, icon: '🔄' },
        { label: 'Total Reps', value: workoutSummary.totalReps, icon: '🎯' },
        {
          label: `Volume (${unit})`,
          value: Math.round(toDisplayWeight(workoutSummary.totalVolume, unit)),
          icon: '⚖️',
        },
        {
          label: 'Duration',
          value: `${workoutSummary.estimatedDuration}m`,
          icon: '⏱️',
        },
      ];

      // Calculate perfect spacing for stats grid
      const statWidth = (width - 240) / 5;
      const statStartX = 120;
      const statY = 380;

      stats.forEach((stat, index) => {
        const statLeft = statStartX + index * statWidth;

        const statBg = new Rect({
          left: statLeft,
          top: statY,
          width: statWidth - 20,
          height: 80,
          fill: 'rgba(255, 255, 255, 0.1)',
          rx: 20,
          ry: 20,
          stroke: 'rgba(255, 255, 255, 0.2)',
          strokeWidth: 1,
        });
        canvas.add(statBg);

        // Icon on the left side
        const statIcon = new Text(stat.icon, {
          left: statLeft + 15,
          top: statY + 40,
          fontSize: 26,
          fill: '#ffffff',
          fontFamily: 'Montserrat, sans-serif',
          originX: 'center',
          originY: 'center',
        });
        canvas.add(statIcon);

        // Stat value on the right side
        const statValue = new Text(stat.value.toString(), {
          left: statLeft + (statWidth - 40),
          top: statY + 40,
          fontSize: 24,
          fontWeight: 'bold',
          fill: '#ffffff',
          fontFamily: 'Montserrat, sans-serif',
          originX: 'center',
          originY: 'center',
        });
        canvas.add(statValue);

        // Stat label centered below
        const statLabel = new Text(stat.label, {
          left: statLeft + (statWidth - 20) / 2,
          top: statY + 65,
          fontSize: 12,
          fill: 'rgba(255, 255, 255, 0.8)',
          fontFamily: 'Montserrat, sans-serif',
          originX: 'center',
          originY: 'center',
        });
        canvas.add(statLabel);
      });

      // 🎯 EXERCISE BREAKDOWN SECTION - Easy positioning!
      const exercisesCard = createCard(60, 520, width - 120, 480);
      const exercisesBg = new Rect(exercisesCard);
      canvas.add(exercisesBg);

      const exercisesTitle = new Text('📋 EXERCISE BREAKDOWN', {
        ...centerText('📋 EXERCISE BREAKDOWN', 560, 26, 'bold'),
      });
      canvas.add(exercisesTitle);

      // Display top 4 exercises with perfect spacing
      const topExercises = workoutToShare.exercises.slice(0, 4);
      const exercisePositions = calculateSpacing(
        610,
        80,
        20,
        topExercises.length
      );

      topExercises.forEach((exercise, index) => {
        const exerciseY = exercisePositions[index];

        const exerciseBg = new Rect({
          left: 100,
          top: exerciseY,
          width: width - 200,
          height: 80,
          fill: 'rgba(255, 255, 255, 0.1)',
          rx: 20,
          ry: 20,
          stroke: 'rgba(255, 255, 255, 0.15)',
          strokeWidth: 1,
        });
        canvas.add(exerciseBg);

        const exerciseNumber = new Text(`${index + 1}`, {
          left: 130,
          top: exerciseY + 40,
          fontSize: 22,
          fontWeight: 'bold',
          fill: 'var(--primary, #8b5cf6)',
          fontFamily: 'Montserrat, sans-serif',
          originX: 'center',
          originY: 'center',
        });
        canvas.add(exerciseNumber);

        const exerciseName = new Text(exercise.name, {
          left: 180,
          top: exerciseY + 25,
          fontSize: 20,
          fontWeight: 'bold',
          fill: '#ffffff',
          fontFamily: 'Montserrat, sans-serif',
        });
        canvas.add(exerciseName);

        // Exercise stats with perfect alignment
        const exerciseStats = exercise.data;
        const totalSets = exerciseStats.length;
        const totalReps = exerciseStats.reduce((acc, set) => acc + (set.reps || 0), 0);
        const maxWeight = Math.max(...exerciseStats.map((set) => set.load || 0));

        const exerciseStatsText = new Text(
          `${totalSets} sets • ${totalReps} reps • ${toDisplayWeight(
            maxWeight,
            unit
          )} ${unit} max`,
          {
            left: 180,
            top: exerciseY + 55,
            fontSize: 15,
            fill: 'rgba(255, 255, 255, 0.7)',
            fontFamily: 'Montserrat, sans-serif',
          }
        );
        canvas.add(exerciseStatsText);
      });

      // 🎯 PERFORMANCE RATING SECTION - Easy centering!
      const performanceCard = createCard(60, 1040, width - 120, 140);
      const performanceBg = new Rect(performanceCard);
      canvas.add(performanceBg);

      const performanceTitle = new Text('⭐ PERFORMANCE RATING', {
        ...centerText('⭐ PERFORMANCE RATING', 1070, 26, 'bold'),
      });
      canvas.add(performanceTitle);

      // Calculate performance score with perfect positioning
      const performanceScore = Math.min(
        100,
        Math.round(
          (workoutSummary.totalVolume / 1000) * 20 +
            workoutSummary.totalSets * 2
        )
      );
      const performanceStars = '⭐'.repeat(
        Math.min(5, Math.ceil(performanceScore / 20))
      );

      const performanceStarsText = new Text(performanceStars, {
        left: 120,
        top: 1110,
        fontSize: 36,
        fill: '#ffd700',
        fontFamily: 'Montserrat, sans-serif',
      });
      canvas.add(performanceStarsText);

      const performanceScoreText = new Text(`${performanceScore}/100`, {
        left: 500,
        top: 1110,
        fontSize: 28,
        fontWeight: 'bold',
        fill: '#ffffff',
        fontFamily: 'Montserrat, sans-serif',
      });
      canvas.add(performanceScoreText);

      // 🎯 FOOTER SECTION - Perfect centering with helpers!
      const footerCard = createCard(60, height - 160, width - 120, 100, 0.5);
      const footerBg = new Rect(footerCard);
      canvas.add(footerBg);

      const poweredBy = new Text('🚀 Powered by Nubod', {
        ...centerText('🚀 Powered by Nubod', height - 120, 22, 'bold'),
      });
      canvas.add(poweredBy);

      const tagline = new Text('Transform your fitness journey', {
        ...centerText('Transform your fitness journey', height - 90, 18),
      });
      canvas.add(tagline);

      // Convert to image and download
      const dataURL = canvas.toDataURL({
        format: 'png',
        quality: 1,
      });

      const link = document.createElement('a');
      link.download = `workout-${format}-${moment().format('YYYY-MM-DD')}.png`;
      link.href = dataURL;
      link.click();

      // Clean up
      canvas.dispose();
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const workoutSummary = getWorkoutSummary();

  // Check if current user owns the workout
  const isWorkoutOwner =
    user && workoutToShare && workoutToShare.client._id === user._id;

  return (
    <div className='workout-share'>
      {getWorkoutsLoading ? (
        <div className='loading-container'>
          <Loading />
        </div>
      ) : (
        <div className='workout-share-container' id='workout-share-container'>
          <>
            {/* Back to Workouts Button - Only visible to workout owner */}
            {isWorkoutOwner && (
              <div className='back-to-workouts'>
                <button
                  className='back-button'
                  onClick={() => window.history.back()}
                >
                  <ArrowLeftOutlined />
                  <span>Back to Workouts</span>
                </button>
              </div>
            )}

            {/* Workout Title and Date */}
            <div className='workout-share-header'>
              {/* Client Name */}
              {workoutToShare && workoutToShare.client && (
                <div className='client-name-display'>
                  <span className='client-name-label'>
                    Completed by {workoutToShare.client.name}{' '}
                    {workoutToShare.client.lastName} 💪
                  </span>
                </div>
              )}

              <h1 className='workout-share-title'>
                {workoutToShare && workoutToShare.name}
              </h1>
              <p className='workout-share-date'>
                {workoutToShare && workoutToShare.date
                  ? moment(workoutToShare.date).format('MMMM D, YYYY')
                  : ''}
              </p>
            </div>

            {/* Download Options - Only visible to workout owner */}
            {isWorkoutOwner && (
              <div className='download-options-top'>
                {/* <Tooltip title='Download for Instagram Story (1080x1920)'>
                  <div
                    className='download-icon story-icon'
                    onClick={() => generateProfessionalImage('instagram-story')}
                  >
                    <InstagramOutlined />
                  </div>
                </Tooltip>
                <Tooltip title='Download for Instagram Post (1080x1080)'>
                  <div
                    className='download-icon post-icon'
                    onClick={() => generateProfessionalImage('instagram-post')}
                  >
                    <InstagramOutlined />
                  </div>
                </Tooltip>
                <Tooltip title='Download for X (1200x675)'>
                  <div
                    className='download-icon x-icon'
                    onClick={() => generateProfessionalImage('twitter')}
                  >
                    <TwitterOutlined />
                  </div>
                </Tooltip> */}
                {(workoutToShare?.status || 'completed') === 'completed' && (
                  <Tooltip title='Share card (story or post)'>
                    <div
                      className='download-icon post-icon'
                      onClick={() => handleShareImage(workoutToShare)}
                      role='button'
                    >
                      <InstagramOutlined />
                    </div>
                  </Tooltip>
                )}
                <Tooltip title='Copy Share Link'>
                  <div
                    className='download-icon share-link-icon'
                    onClick={() => {
                      const shareUrl = `${window.location.origin}/share/my-workout/${workoutToShare?.token}`;
                      navigator.clipboard.writeText(shareUrl);
                      toast.success('Link copied successfully!');
                    }}
                  >
                    <ShareAltOutlined />
                  </div>
                </Tooltip>
              </div>
            )}

            {/* Workout Summary Stats */}
            {isStrengthWorkout(workoutToShare) ? (
              <div className='workout-summary-stats'>
                <div className='summary-grid'>
                  <div className='summary-item'>
                    <div className='summary-value'>
                      {workoutSummary.totalExercises}
                    </div>
                    <div className='summary-label'>Exercises</div>
                  </div>
                  <div className='summary-item'>
                    <div className='summary-value'>
                      {workoutSummary.totalSets}
                    </div>
                    <div className='summary-label'>Total Sets</div>
                  </div>
                  <div className='summary-item'>
                    <div className='summary-value'>
                      {workoutSummary.totalReps}
                    </div>
                    <div className='summary-label'>Total Reps</div>
                  </div>
                  <div className='summary-item'>
                    <div className='summary-value'>
                      {Math.round(toDisplayWeight(workoutSummary.totalVolume, unit))}
                    </div>
                    <div className='summary-label'>Volume ({unit})</div>
                  </div>
                  <div className='summary-item'>
                    <div className='summary-value'>
                      {workoutSummary.estimatedDuration}
                    </div>
                    <div className='summary-label'>Minutes</div>
                  </div>
                </div>
              </div>
            ) : (
              // Activity-style summary: type label + duration + notes. We
              // intentionally drop the exercise/set/volume tiles since the
              // record has no per-set data.
              <div className='workout-summary-stats'>
                <div
                  className='summary-grid'
                  style={{ gridTemplateColumns: '1fr 1fr' }}
                >
                  <div className='summary-item'>
                    <div
                      className='summary-value'
                      style={{ textTransform: 'capitalize' }}
                    >
                      {workoutToShare?.type || 'Activity'}
                    </div>
                    <div className='summary-label'>Type</div>
                  </div>
                  <div className='summary-item'>
                    <div className='summary-value'>
                      {workoutToShare?.duration
                        ? Math.round(workoutToShare.duration / 60)
                        : '—'}
                    </div>
                    <div className='summary-label'>Minutes</div>
                  </div>
                </div>
                {workoutToShare?.notes ? (
                  <div className='workout-share-notes'>
                    {workoutToShare.notes}
                  </div>
                ) : null}
              </div>
            )}

            {/* Exercises Section — strength workouts only. Activity logs
                (cardio/mobility/etc.) have no exercise breakdown to render. */}
            {isStrengthWorkout(workoutToShare) ? (
              <div className='exercises-section'>
                <h2 className='section-title'>Exercise Breakdown</h2>
                {renderExercises()}
              </div>
            ) : null}

            {/* Footer */}
            <div className='workout-footer'>
              <div className='powered-by'>
                <span className='powered-text'>Powered by</span>
                <img src={NubodLogo} alt='Nubod' className='brand-logo' />
              </div>
            </div>
          </>
        </div>
      )}

      {/* Hidden canvas for image generation */}
      <canvas id='temp-canvas' style={{ display: 'none' }} />
      <ShareCardModal
        workout={shareCardOpen ? workoutToShare : null}
        onClose={() => setShareCardOpen(false)}
      />
    </div>
  );
};
